import {
  Pill,
  Tooltip,
  Typography,
  Card,
  CardContent,
  Divider,
  Icon
} from '@sanlam-glacier/app-common/components';
import { ProposalOverviewViewModel } from 'bff';
import { format } from 'date-fns';
import styles from './proposal-card.module.scss';
import clsx from 'clsx';
import { OverviewContextMenu } from 'modules/new-business/components/organisms/overview-context-menu/OverviewContextMenu';
import { useNavigate } from 'react-router';
import { generateEditProposalPath } from 'routes/route-paths';
import { useGetBreakPointReached } from 'hooks/useGetBreakPointReached';
import { Show } from '@sanlam-glacier/app-common/components';
import { Nbsp } from 'components/atoms/html-symbols/Nbsp';
import { truncateClientName } from 'modules/new-business/utils/proposal/truncate-client-name.function';

const CASCADE_BREAK_POINT: number = 1001;

type ProposalCardProps = {
  proposal: ProposalOverviewViewModel;
  disabled: boolean;
};

const baseTypographyProps = {
  align: Typography.ALIGN.LEFT,
  variant: Typography.VARIANT.B4,
  weight: Typography.WEIGHT.NORMAL,
  className: styles.textCell
};

export const ProposalCard = ({ proposal, disabled }: ProposalCardProps) => {
  const navigate = useNavigate();
  const cascade = useGetBreakPointReached(CASCADE_BREAK_POINT);

  const productNameLabelStyle = clsx(styles.cardPropertyLabel, cascade && styles.cardTwoRows);
  const productNameValueStyle = clsx(
    styles.cardPropertyValue,
    cascade && styles.cardTwoRows,
    styles.productNameValue
  );

  const goToProposal = () => {
    navigate(generateEditProposalPath(proposal.id));
  };

  const formatDate = (date: Date | undefined) => {
    return date ? format(date, 'yyyy - MM - dd') : '-';
  };

  return (
    <button onClick={goToProposal} className={styles.noStyleButton}>
      <Card variant={Card.VARIANT.OUTLINED} className={styles.baseCard}>
        <CardContent className={clsx([styles.rowSubgrid, styles.cardContentWrapper])}>
          <div className={styles.headerWrapper}>
            <span className={styles.proposalNumberWrapper}>
              <Icon
                icon={proposal.locked ? 'action_lock' : 'action_lock-open-solid'}
                className={`${styles.proposalIcon} ${proposal.locked ? '' : styles.unlockedProposalIcon}`}
              />
              <Typography
                {...baseTypographyProps}
                variant={Typography.VARIANT.B2}
                weight={Typography.WEIGHT.BOLD}
              >
                Proposal #:&nbsp;
              </Typography>

              <Typography
                {...baseTypographyProps}
                color={Typography.COLOR.PRIMARY}
                weight={Typography.WEIGHT.MEDIUM}
                variant={Typography.VARIANT.B2}
              >
                {proposal.id}
              </Typography>
            </span>
            <div className={styles.contextMenuWrapper}>
              {!cascade && (
                <Pill size={Pill.SIZE.SMALL} disabled={proposal.locked}>
                  {proposal.statusId}
                </Pill>
              )}
              <div>
                <OverviewContextMenu
                  proposalId={proposal.id}
                  isLocked={proposal.locked ?? false}
                  isDisabled={disabled}
                />
              </div>
            </div>
          </div>
          {cascade && (
            <div className={styles.detachedStatus}>
              <Pill size={Pill.SIZE.SMALL} disabled={proposal.locked}>
                {proposal.statusId}
              </Pill>
            </div>
          )}

          <Divider margin size={Divider.SIZE.MEDIUM} />
          <div>
            <div className={styles.rowWrapper}>
              <span className={styles.cardPropertyLabel}>Created</span>
              <span className={styles.cardPropertyValue}>{formatDate(proposal.dateCreated)}</span>
            </div>
            <div className={styles.rowWrapper}>
              <span className={styles.cardPropertyLabel}>Client name </span>
              <Tooltip content={proposal.clientName}>
                <span className={styles.cardPropertyValue}>{truncateClientName(proposal.clientName)}</span>
              </Tooltip>
            </div>
            <div className={styles.rowWrapper}>
              <span className={productNameLabelStyle}>Product name</span>
              <span className={productNameValueStyle}>{proposal.productName}</span>
            </div>
            <div className={styles.rowWrapper}>
              <span className={styles.cardPropertyLabel}>Proposal Value</span>
              <span className={styles.cardPropertyValue}>
                {proposal.proposalLumpSumValue?.ToCurrencyString({ excludePrefix: true })}
              </span>
              <Show when={proposal.proposalRecurringValue !== undefined}>
                <span className={styles.cardPropertyValue}>
                  <Nbsp />
                  {`[${proposal.proposalRecurringValue?.ToCurrencyString({ excludePrefix: true })} recur]`}
                </span>
              </Show>
            </div>
            <div className={styles.rowWrapper}>
              <span className={styles.cardPropertyLabel}>Currency</span>
              <span className={styles.cardPropertyValue}>{proposal.currency}</span>
            </div>
            <div className={styles.rowWrapper}>
              <span className={styles.cardPropertyLabel}>Last opened</span>
              <span className={styles.cardPropertyValue}>{formatDate(proposal.lastOpened)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </button>
  );
};
